import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  CardSkeleton,
  DetailRowsSkeleton,
  InlineSkeleton,
  PageHeaderSkeleton,
  UserNameCell,
} from '@/components/core';
import { Badge } from '@/components/ui-kit/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { ASSESSMENT_TYPES, PEER_CATEGORIES, REVIEW_TYPES } from '@/modules/shared/hr-form-engine';
import {
  useDepartmentOptions,
  useDesignationOptions,
  useEmployeesByUserIds,
} from '@/modules/shared/hr-lookups';
import {
  formatDate,
  getApplicableReviewTypes,
  useGetPmReviewById,
  useGetPmReviewDetailsForReview,
} from '@/modules/performance/hr-review-core';
import { REVIEW_STATUS_VARIANT, reviewStatusKey } from '../../constants/review-status';

const DetailRow = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="min-w-0 font-medium [overflow-wrap:anywhere] sm:text-right">
      {value || '-'}
    </span>
  </div>
);

export const ReviewDetailsPage = () => {
  const { t } = useTranslation();
  const { reviewId } = useParams<{ reviewId: string }>();

  const { data: review, isLoading } = useGetPmReviewById(reviewId);
  const { data: details = [], isLoading: isDetailsLoading } =
    useGetPmReviewDetailsForReview(reviewId);
  const { data: departments = [], isLoading: isDepartmentsLoading } = useDepartmentOptions();
  const { data: designations = [], isLoading: isDesignationsLoading } = useDesignationOptions();

  const reviewerIds = useMemo(() => details.map((detail) => detail.PeerUserId), [details]);
  const { byUserId, isLoading: isNamesLoading } = useEmployeesByUserIds(
    review ? [review.ReviewForId, ...reviewerIds] : reviewerIds
  );

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-4" aria-busy="true">
        <PageHeaderSkeleton subtitle={false} />
        <CardSkeleton lines={6} />
        <CardSkeleton lines={4} />
      </div>
    );
  }
  if (!review) return <p className="text-sm text-muted-foreground">{t('REVIEW_NOT_FOUND')}</p>;

  const steps = getApplicableReviewTypes(review);
  const assessment = ASSESSMENT_TYPES.find((type) => type.key === review.AssessmentType);
  const department = departments.find((option) => option.ItemId === review.DepartmentId)?.Title;
  const designation = designations.find((option) => option.ItemId === review.DesignationId)?.Title;
  const reviewTypeTitle = (key: string) =>
    t(REVIEW_TYPES.find((type) => type.key === key)?.title ?? key);
  const annualOutcome = details.find(
    (detail) =>
      detail.ReviewType === 'annual' && (detail.SuggestedTier || detail.PromotionRecommendation)
  );
  const isMbmSent = details.some((detail) => detail.ReviewType === 'annual' && detail.IsMbmSent);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">{review.Name}</h1>
        {review.AssessmentType === 'arf' && (
          <Link
            className="text-sm text-primary underline"
            to={`/hr/performance-dashboard?userId=${review.ReviewForId}`}
          >
            {t('VIEW_PERFORMANCE_DASHBOARD')}
          </Link>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t(assessment?.title ?? review.AssessmentType)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Names are looked up: placeholders rather than "-" or a fallback until they load. */}
          <DetailRow
            label={t('EMPLOYEE_NAME')}
            value={
              isNamesLoading ? (
                <InlineSkeleton className="w-32" />
              ) : (
                (byUserId.get(review.ReviewForId)?.DisplayName ?? review.Name)
              )
            }
          />
          <DetailRow
            label={t('DEPARTMENT')}
            value={isDepartmentsLoading ? <InlineSkeleton className="w-28" /> : department}
          />
          <DetailRow
            label={t('DESIGNATION')}
            value={isDesignationsLoading ? <InlineSkeleton className="w-28" /> : designation}
          />
          <DetailRow label={t('REQUEST_DATE')} value={formatDate(review.CreatedDate)} />

          <div className="flex flex-col gap-2 pt-2">
            {steps.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('NO_REVIEW_TYPES_APPLICABLE')}</p>
            )}
            {steps.map(({ reviewType, status, deadline }) => (
              <div
                key={reviewType}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm"
              >
                <span>{reviewTypeTitle(reviewType)}</span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {deadline ? `${t('DEADLINE')}: ${formatDate(deadline)}` : t('NO_DEADLINE')}
                  </span>
                  <Badge variant={REVIEW_STATUS_VARIANT[status]}>
                    {t(reviewStatusKey(status))}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('REVIEWERS')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isDetailsLoading ? (
            <DetailRowsSkeleton rows={3} />
          ) : details.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('NO_REVIEWERS_YET')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('REVIEWER')}</TableHead>
                  <TableHead>{t('REVIEW_TYPE')}</TableHead>
                  <TableHead>{t('CATEGORY')}</TableHead>
                  <TableHead>{t('DEADLINE')}</TableHead>
                  <TableHead>{t('STATUS')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {details.map((detail) => (
                  <TableRow key={detail.ItemId}>
                    <TableCell>
                      {isNamesLoading ? (
                        <InlineSkeleton className="w-32" />
                      ) : (
                        <UserNameCell
                          name={byUserId.get(detail.PeerUserId)?.DisplayName ?? detail.PeerUserId}
                          email={byUserId.get(detail.PeerUserId)?.Email}
                        />
                      )}
                    </TableCell>
                    <TableCell>{reviewTypeTitle(detail.ReviewType)}</TableCell>
                    <TableCell>
                      {detail.ReviewType === 'peer'
                        ? t(
                            PEER_CATEGORIES.find((category) => category.key === detail.Category)
                              ?.title ?? ''
                          )
                        : '-'}
                    </TableCell>
                    <TableCell>{formatDate(detail.PeersDeadLine)}</TableCell>
                    <TableCell>
                      <Badge variant={detail.Status === 'Completed' ? 'default' : 'secondary'}>
                        {t(detail.Status.toUpperCase())}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {review.AssessmentType === 'arf' && (annualOutcome || isMbmSent) && (
        <Card>
          <CardHeader>
            <CardTitle>{t('PROMOTION_OUTCOME')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <DetailRow label={t('SUGGESTED_TIER')} value={annualOutcome?.SuggestedTier} />
            <DetailRow
              label={t('PROMOTION_RECOMMENDATION')}
              value={annualOutcome?.PromotionRecommendation}
            />
            <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-sm">
              <span className="text-muted-foreground">{t('SENT_TO_MBM')}</span>
              <Badge variant={isMbmSent ? 'default' : 'secondary'}>
                {t(isMbmSent ? 'YES' : 'NO')}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {review.SelfOrPeerReviewSummary && (
        <Card>
          <CardHeader>
            <CardTitle>{t('REVIEW_SUMMARY')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line text-sm">{review.SelfOrPeerReviewSummary}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
